// Regras puras do módulo de redescoberta (testadas em node: web/test/ui-rediscover.test.ts e rules-editor.test.ts). Sem DOM, sem store.
import { isQuoteText } from "../../../../shared/text.ts";
import { normalizeTag } from "../../../../shared/normalize.ts";
import type { Card, CardFields, Doc } from "../../../../shared/types.ts";

const DAY = 86_400_000;

/** Reencontro (§7.8): vivos, não privados, guardados há 7+ dias e sem "manter" nos últimos 30. */
export const eligible = (cards: readonly Card[], now: number) =>
  cards.filter((c) => !c.deletedAt && !c.isPrivate && now - c.createdAt >= 7 * DAY && (c.lastReviewedAt === null || now - c.lastReviewedAt >= 30 * DAY));

/** Sorteio sem reposição com peso √idade_em_dias × (2 se nunca revisto) (Efraimidis–Spirakis: chave u^(1/peso)). */
export function draw(pool: readonly Card[], n: number, now: number, rnd = Math.random): Card[] {
  const weight = (c: Card) => Math.sqrt(Math.max(1, (now - c.createdAt) / DAY)) * (c.lastReviewedAt === null ? 2 : 1);
  return pool
    .map((c) => [rnd() ** (1 / weight(c)), c] as const)
    .sort((a, b) => b[0] - a[0])
    .slice(0, n)
    .map(([, c]) => c);
}

type Json = { type?: string; text?: string; marks?: { type: string }[]; attrs?: Record<string, unknown>; content?: Json[] };
/** Itens de checklist da nota (aninhados também). */
export function taskStats(doc: Doc | null): { total: number; done: number } {
  const out = { total: 0, done: 0 };
  const walk = (n: Json) => {
    if (n.type === "taskItem") {
      out.total++;
      if (n.attrs?.checked) out.done++;
    }
    n.content?.forEach(walk);
  };
  if (doc) walk(doc as Json);
  return out;
}

/** Citação (§4.2): a regra mora em shared/text.ts, a mesma da captura (src/routes/capture.ts). */
export { isQuoteText };

const allDone = (s: { total: number; done: number }) => s.total > 0 && s.done === s.total;

/**
 * Campos que um texto de nota salvo deriva, só nas viradas: o que a pessoa escolheu à mão fica (§4.2, fase 3 aceite 7).
 * - checklist que aparece numa nota sem ação → "fazer" (tirou a ação com a checklist já lá: não volta);
 * - itens todos marcados ↔ não: "feito" segue a virada dos itens ("Feito" manual fica enquanto os itens não viram);
 * - texto entre aspas → citação, só enquanto o tipo não é escolha da pessoa (kindOrigin 'user'); o contrário nunca;
 * - texto só com códigos #rrggbb → cor (um) ou paleta (vários), com a mesma regra; num card de cor a paleta segue o texto.
 *   Card de cor cujo texto deixou de ser só códigos perde a paleta (as amostras escondiam o texto) e, se o tipo não é
 *   escolha da pessoa, volta a nota.
 */
export function noteFields(
  c: Pick<Card, "kind" | "kindOrigin" | "action" | "completedAt" | "content"> & { palette?: Card["palette"] },
  doc: Doc,
  text: string,
  now = Date.now(),
): Partial<CardFields> {
  const f: Partial<CardFields> = {};
  const was = taskStats(c.content);
  const t = taskStats(doc);
  const action = c.action ?? (t.total && !was.total ? (f.action = "fazer") : null);
  if (action === "fazer" && t.total && allDone(t) !== allDone(was) && allDone(t) !== !!c.completedAt) f.completedAt = allDone(t) ? now : null;
  const auto = c.kind === "note" && c.kindOrigin !== "user";
  const hexes = hexCodes(text);
  if (auto && isQuoteText(text)) f.kind = "quote";
  else if (hexes && (c.kind === "color" || auto)) {
    if (c.kind !== "color") f.kind = "color";
    const palette = Object.fromEntries(hexes.map((h) => [h, Math.round(1000 / hexes.length) / 1000]));
    if (JSON.stringify(c.palette ?? null) !== JSON.stringify(palette)) Object.assign(f, { palette, dominantColor: hexes[0] });
  } else if (c.kind === "color" && !hexes) {
    if (c.kindOrigin !== "user") f.kind = "note";
    if (c.palette) Object.assign(f, { palette: null, dominantColor: null });
  }
  return f;
}

/** noteFields com a origem: tipo derivado do texto (citação, cor) segue automático, nunca "escolha sua" (D10). */
export function autoFields(...args: Parameters<typeof noteFields>): Partial<CardFields> {
  const f = noteFields(...args);
  return f.kind ? { ...f, kindOrigin: "source" } : f;
}

const HEX = /^#[0-9a-f]{6}$/i;
/** Texto feito só de códigos #rrggbb (separados por espaço, vírgula, ponto e vírgula ou linha), de 1 a 32: a lista, sem repetir. */
export function hexCodes(text: string): string[] | null {
  const parts = text.trim().toLowerCase().split(/[\s,;]+/);
  if (!parts.every((p) => HEX.test(p))) return null;
  const out = [...new Set(parts)];
  return out.length <= 32 ? out : null; // o servidor aceita até 32 cores numa paleta
}

/** Cores de um card de cor: os códigos do texto; a paleta gravada só sem texto (importado). Texto que não é cor: nenhuma. */
export const cardColors = (c: Pick<Card, "palette" | "contentText">): string[] =>
  c.contentText?.trim() ? (hexCodes(c.contentText) ?? []) : Object.keys(c.palette ?? {}).filter((h) => HEX.test(h));

/** Texto do doc fora de código (bloco ou marca), com quebra entre blocos: #etiqueta dentro de código não conta. */
const plain = (n: Json): string =>
  n.type === "text" ? (n.marks?.some((m) => m.type === "code") ? " " : (n.text ?? "")) : n.type === "codeBlock" ? "\n" : `\n${(n.content ?? []).map(plain).join("")}\n`;

const WORD = String.raw`[\p{L}\p{N}_-]*\p{L}[\p{L}\p{N}_-]*`; // ao menos uma letra: #1 não é etiqueta
const TAG = new RegExp(String.raw`(?<=^|[\s(,;])#(${WORD})`, "gu");
const TAG_WORD = new RegExp(`^#${WORD}$`, "u");
const tagOf = (word: string) => (HEX.test(`#${word}`) ? null : word); // #c0ffee é cor, não etiqueta

/**
 * #palavra na nota rápida vira etiqueta ao guardar (§4.1 "adicionar no salvamento"). No meio do texto ela fica (faz
 * parte da frase); um último parágrafo só de etiquetas (lista no fim) sai do texto. Se a nota ficaria vazia, nada sai.
 */
export function noteTags(doc: Doc, text: string): { tags: string[]; doc: Doc; text: string } {
  const byKey = new Map<string, string>();
  for (const m of plain(doc as Json).matchAll(TAG)) {
    const t = tagOf(m[1]);
    if (t && !byKey.has(normalizeTag(t))) byKey.set(normalizeTag(t), t);
  }
  const tags = [...byKey.values()];
  const blocks = (doc.content ?? []) as Json[];
  const last = blocks.at(-1);
  const line = last?.type === "paragraph" ? plain(last).trim() : "";
  const onlyTags = !!line && line.split(/[\s,;]+/).every((w) => TAG_WORD.test(w) && !!tagOf(w.slice(1)));
  const rest: Doc = { ...doc, content: blocks.slice(0, -1) };
  if (!onlyTags || isBlank(rest, plain(rest as Json))) return { tags, doc, text };
  const trimmed = text.trimEnd();
  return { tags, doc: rest, text: trimmed.endsWith(line) ? trimmed.slice(0, -line.length).trimEnd() : trimmed };
}

/** Nota sem texto nem imagem: não vale guardar. */
export const isBlank = (doc: Doc, text: string) => !text.trim() && !JSON.stringify(doc).includes('"type":"image"');

/** Endereço digitado no campo de link: sem esquema e com ponto ganha https://; só http(s) e mailto passam. */
export function linkHref(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  const withScheme = /^[a-z][a-z\d+.-]*:/i.test(s) ? s : /^[^\s/]+\.[^\s]{2,}/.test(s) ? `https://${s}` : null;
  if (!withScheme) return null;
  try {
    const u = new URL(withScheme);
    return /^(https?|mailto):$/.test(u.protocol) ? u.href : null;
  } catch {
    return null;
  }
}
